import type {
  LegalCitation,
  LegalPassage,
  LegalSource,
  LegalVersion,
  SourceKind,
} from '@buakei/types';
import { DEMO_SOURCES } from '@/demo/legal-sources';
import { demoResponse } from './demo-runtime';

export type ResolvedCitation =
  | { status: 'resolved'; source: LegalSource; version: LegalVersion; passages: LegalPassage[] }
  | { status: 'unresolved'; reason: string };

export type PassageHit = { source: LegalSource; version: LegalVersion; passage: LegalPassage };

/** Contract the Worker-backed legal source service will implement in M3/M4. */
export interface LegalSourceService {
  listSources(): Promise<LegalSource[]>;
  resolveCitation(citation: LegalCitation): Promise<ResolvedCitation>;
  searchPassages(query: string, kinds: SourceKind[]): Promise<PassageHit[]>;
}

/**
 * Deterministic resolution (LEG-006 semantics): a citation is evidence only if its exact
 * source, version and every passage ID exist. Anything else is unresolved — never "verified".
 */
export function resolveCitationSync(
  citation: LegalCitation,
  sources: readonly LegalSource[],
): ResolvedCitation {
  const source = sources.find((s) => s.id === citation.sourceId);
  if (!source)
    return { status: 'unresolved', reason: 'The cited source is not in the searched collection.' };
  const version = source.versions.find((v) => v.id === citation.versionId);
  if (!version)
    return { status: 'unresolved', reason: 'The cited source version could not be found.' };
  const passages = citation.passageIds.map((id) => version.passages.find((p) => p.id === id));
  if (passages.some((p) => p === undefined)) {
    return {
      status: 'unresolved',
      reason: 'One or more cited passages do not exist in this version.',
    };
  }
  return { status: 'resolved', source, version, passages: passages as LegalPassage[] };
}

export const demoLegalSourceService: LegalSourceService = {
  listSources: () => demoResponse(DEMO_SOURCES, []),

  resolveCitation: async (citation) =>
    demoResponse(resolveCitationSync(citation, DEMO_SOURCES), {
      status: 'unresolved',
      reason: 'No sources are available in this view.',
    }),

  searchPassages: async (query, kinds) => {
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    const hits: PassageHit[] = [];
    for (const source of DEMO_SOURCES) {
      if (!kinds.includes(source.kind)) continue;
      for (const version of source.versions) {
        for (const passage of version.passages) {
          const haystack = `${source.title} ${passage.label} ${passage.text}`.toLowerCase();
          if (terms.length > 0 && terms.every((t) => haystack.includes(t))) {
            hits.push({ source, version, passage });
          }
        }
      }
    }
    return demoResponse(hits, []);
  },
};
